import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useEffect } from 'react';
import {
  COMMAND_PRIORITY_LOW,
  KEY_ENTER_COMMAND,
  $getSelection,
  $isRangeSelection,
  $createParagraphNode,
  $createTextNode,
} from 'lexical';

export default function ClearFormattingOnEnterPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event) => {
        const selection = $getSelection();

        if (!$isRangeSelection(selection)) {
          return false;
        }

        // Get the current node
        const nodes = selection.getNodes();
        if (nodes.length === 0) return false;

        const currentNode = nodes[0];

        // Check if the current text node has inline styles
        if (currentNode.getType() === 'text') {
          const style = currentNode.getStyle();

          // If there are inline styles (color, background-color, etc.)
          if (style) {
            // Let the default Enter behavior happen first
            setTimeout(() => {
              editor.update(() => {
                const newSelection = $getSelection();
                if ($isRangeSelection(newSelection)) {
                  const newNodes = newSelection.getNodes();

                  // Clear styles from the new line's text nodes
                  newNodes.forEach((node) => {
                    if (node.getType() === 'text' && node.getStyle()) {
                      // Create a new text node without styles
                      const textContent = node.getTextContent();
                      const newTextNode = $createTextNode(textContent);

                      // Copy format (bold, italic, etc.) but not inline styles
                      newTextNode.setFormat(node.getFormat());

                      // Replace the styled node with unstyled one
                      node.replace(newTextNode);
                    }
                  });
                }
              });
            }, 0);
          }
        }

        // Fallback: ensure Enter creates a new paragraph for normal text blocks (Notion-like)
        try {
          const anchor = selection.anchor;
          const topLevelElement = anchor.getNode().getTopLevelElementOrThrow();
          // Only handle plain paragraphs; don't interfere with lists, checklist items, tables, etc.
          if (topLevelElement && topLevelElement.getType() === 'paragraph') {
            // Only take over when caret is at end of the paragraph and selection is collapsed
            const isCollapsed = typeof selection.isCollapsed === 'function' ? selection.isCollapsed() : selection.anchor.equals(selection.focus);
            const anchorNode = anchor.getNode();
            const isAtEnd = anchorNode.getType && anchorNode.getType() === 'text'
              ? anchor.offset === anchorNode.getTextContent().length
              : true;

            if (!isCollapsed || !isAtEnd) {
              return false; // let default splitting behavior handle mid-paragraph Enter
            }

            if (event && typeof event.preventDefault === 'function') {
              event.preventDefault();
            }
            editor.update(() => {
              const newParagraph = $createParagraphNode();
              topLevelElement.insertAfter(newParagraph);
              newParagraph.select();
            });
            return true;
          }
        } catch (e) {
          // No-op: if we can't resolve top level element, let default behavior run
        }

        return false; // Let default Enter behavior continue
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor]);

  return null;
}
